/**
 * EmailGateModal – global modal som åpnes når backend returnerer
 * { error_code: "EMAIL_NOT_VERIFIED" }. Forteller brukeren at e-posten
 * må bekreftes, og lar dem be om ny verifiseringsmail i ett klikk.
 *
 * Lyttes via window-event "ne:email-gate" (sendt fra api.js interceptor).
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Mail, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function EmailGateModal() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onGate = (e) => {
      setEmail(e?.detail?.email || null);
      setOpen(true);
    };
    window.addEventListener("ne:email-gate", onGate);
    return () => window.removeEventListener("ne:email-gate", onGate);
  }, []);

  const close = () => setOpen(false);

  const resend = async () => {
    setSending(true);
    try {
      await api.post("/auth/verify-email/resend");
      toast.success(t("email_gate.resend_ok"));
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("email_gate.resend_err"));
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      data-testid="email-gate-modal"
      onClick={close}
    >
      <div
        className="max-w-md w-full panel-tight p-6 border-2 border-[var(--magenta)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={close}
          className="absolute right-3 top-3 text-[var(--cream-faint)] hover:text-[var(--magenta)] p-1"
          aria-label={t("system_msg.close_label")}
          data-testid="email-gate-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3 mb-4">
          <AlertTriangle className="h-6 w-6 text-[var(--magenta)] shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--magenta)] mb-1">
              {t("email_gate.label")}
            </div>
            <h2 className="font-fraunces italic text-2xl text-[var(--cream)]">
              {t("email_gate.title")}
            </h2>
          </div>
        </div>

        <p className="text-[var(--cream-soft)] mb-4 leading-relaxed">
          {email ? (
            <>
              {t("email_gate.body_with_email_a")}{" "}
              <span className="text-[var(--cream)] font-meta">{email}</span>
              {t("email_gate.body_with_email_b")}
            </>
          ) : (
            t("email_gate.body_no_email")
          )}
        </p>

        <p className="text-[var(--cream-faint)] text-sm mb-6">
          {t("email_gate.tip")}
        </p>

        <div className="flex gap-2">
          <button
            onClick={resend}
            disabled={sending}
            className="btn-magenta flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-40"
            data-testid="email-gate-resend"
          >
            <Mail className="h-4 w-4" />
            {sending ? t("email_gate.sending") : t("email_gate.resend_btn")}
          </button>
          <button
            onClick={close}
            className="px-4 py-2 border border-white/20 text-[var(--cream-soft)] hover:border-white/40 font-meta"
            data-testid="email-gate-cancel"
          >
            {t("common.cancel")}
          </button>
        </div>
      </div>
    </div>
  );
}
